import React from 'react';
import { FunnelIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { FormSelect } from './FormSelect';
import { FormInput } from './FormInput';
import { Category, Destination, Person } from '../types/budget';
import { getDisplayLabel } from '../utils/budget';
import { normalizeResponsible, normalizeDestination } from '../utils/normalization';
import { useTranslation } from 'react-i18next';
import { TransactionFiltersState } from '../hooks/useTransactionFilters';

interface TransactionFiltersProps {
  filters: TransactionFiltersState;
  setFilters: (filters: Partial<TransactionFiltersState>) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  transactionCount: number;
  showAddButton?: boolean;
  onAddClick?: (type: 'income' | 'expense') => void;
  categories: { id: string; name: string }[]; // Use the categories from the budget
  responsibles: { id: string; name: string }[]; // Use the responsibles from the budget
  destinations: { id: string; name: string }[]; // Use the destinations from the budget
}

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  transactionCount,
  showAddButton = true,
  onAddClick,
  categories = [],
  responsibles = [],
  destinations = [],
}) => {
  const { t } = useTranslation();

  // Convert categories, responsibles, and destinations to the format expected by FormSelect
  const categoryOptions = categories
    .map((category) => ({
      value: category.id,
      label: getDisplayLabel(category.id, categories),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const responsibleOptions = responsibles
    .map((responsible) => {
      const normalizedId = normalizeResponsible(responsible.id);
      return {
        value: normalizedId,
        label:
          normalizedId === 'common'
            ? getDisplayLabel('common', [{ id: 'common', name: t('defaults.responsibles.common') }])
            : getDisplayLabel(responsible.id, responsibles),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const destinationOptions = destinations
    .map((destination) => {
      const normalizedId = normalizeDestination(destination.id);
      return {
        value: normalizedId,
        label:
          normalizedId === 'cash'
            ? getDisplayLabel('cash', [{ id: 'cash', name: t('defaults.destinations.cash') }])
            : getDisplayLabel(destination.id, destinations),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="rounded-lg bg-white px-6 pt-6 dark:bg-surface-800">
      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center rounded-lg border border-slate-500 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-700 dark:text-slate-300 dark:hover:bg-surface-600"
          >
            <FunnelIcon className="mr-2 h-5 w-5" />
            {t('transaction.filters.title')}
          </button>
          <div className="rounded-lg bg-slate-50 px-4 py-2 text-sm font-medium text-slate-500 dark:bg-surface-700 dark:text-slate-400">
            {t('transaction.count', { count: transactionCount })}
          </div>
        </div>
        {showAddButton && onAddClick && (
          <div className="flex space-x-3">
            <button
              onClick={() => onAddClick('income')}
              data-attr="add-income-button"
              className="flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              {t('transaction.newIncome')}
            </button>
            <button
              onClick={() => onAddClick('expense')}
              data-attr="add-expense-button"
              className="flex items-center rounded-md border border-transparent bg-indigo-800 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-800 focus:ring-offset-2 dark:bg-indigo-800 dark:text-white dark:hover:bg-indigo-700"
            >
              <MinusIcon className="mr-2 h-5 w-5" />
              {t('transaction.newExpense')}
            </button>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="mb-6 space-y-4 rounded-lg bg-slate-50 p-6 dark:bg-surface-900">
          <FormInput
            label={t('transaction.filters.search')}
            value={filters.search}
            onChange={(value) => setFilters({ search: value as string })}
            placeholder={t('transaction.filters.searchPlaceholder')}
            hideLabel
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <FormSelect
              label={t('transaction.category')}
              value={filters.category}
              onChange={(value) => setFilters({ category: value as Category })}
              options={categoryOptions}
              placeholder={t('transaction.filters.allCategories')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.responsible')}
              value={filters.responsible}
              onChange={(value) => setFilters({ responsible: value as Person })}
              options={responsibleOptions}
              placeholder={t('transaction.filters.allResponsibles')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.destination')}
              value={filters.destination}
              onChange={(value) => setFilters({ destination: value as Destination })}
              options={destinationOptions}
              placeholder={t('transaction.filters.allDestinations')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.amount')}
              value={filters.amountType}
              onChange={(value) => setFilters({ amountType: value as '' | 'income' | 'expense' })}
              options={[
                { value: 'income', label: t('transaction.filters.incomesOnly') },
                { value: 'expense', label: t('transaction.filters.expensesOnly') },
              ].sort((a, b) => a.label.localeCompare(b.label))}
              placeholder={t('transaction.filters.allAmounts')}
              hideLabel
            />
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() =>
                setFilters({
                  category: '',
                  responsible: '',
                  destination: '',
                  amountType: '',
                  search: '',
                })
              }
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              {t('transaction.filters.reset')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
